// Chuông theo phẩm khi nhặt đồ (ITEMS §3.4, UI_SPEC §5.4): tổng hợp bằng WebAudio, không dùng file.
// Phàm im · Hoàng mõ gỗ · Huyền chuông đồng nhỏ · Địa khánh ngọc · Thiên chuông chùa xa · Tiên đại hồng
// chung · Hỗn Độn 0,6s im lặng tuyệt đối. Rung máy theo thang phản hồi (UI_SPEC §12).
import { settings } from '@state/modules/settings';

let ctx: AudioContext | null = null;
let unlockBound = false;

/** Tạo / đánh thức AudioContext: chỉ gọi trong một cử chỉ của người chơi (iOS giữ suspended nếu không). */
function unlock(): void {
  if (typeof AudioContext === 'undefined') return;
  ctx ??= new AudioContext();
  if (ctx.state === 'suspended') void ctx.resume();
}

/**
 * main.ts gọi một lần: mỗi lần chạm / phím đều đánh thức lại context (iOS tự treo sau cuộc gọi, khóa
 * máy). Chuông gọi từ effect (không phải cử chỉ) chỉ kêu khi context đã chạy.
 */
export function bindAudioUnlock(target: Window = window): void {
  if (unlockBound) return;
  unlockBound = true;
  target.addEventListener('pointerdown', unlock, { passive: true });
  target.addEventListener('touchend', unlock, { passive: true });
  target.addEventListener('keydown', unlock);
}

function audio(): AudioContext | null {
  return ctx && ctx.state === 'running' ? ctx : null;
}

interface BellDef {
  f: number;
  partials: readonly number[];
  decay: number;
  gain: number;
  type: OscillatorType;
}

const BELLS: readonly (BellDef | null)[] = [
  null,
  { f: 820, partials: [1, 2.4], decay: 0.12, gain: 0.25, type: 'triangle' },
  { f: 1320, partials: [1, 2.76, 5.4], decay: 0.7, gain: 0.18, type: 'sine' },
  { f: 1760, partials: [1, 2.1, 3.9], decay: 1.1, gain: 0.16, type: 'sine' },
  { f: 330, partials: [1, 2.02, 3.01, 4.2], decay: 2.2, gain: 0.22, type: 'sine' },
  { f: 110, partials: [1, 2.0, 2.99, 4.1, 5.4], decay: 3.2, gain: 0.3, type: 'sine' },
  null,
];

export function bell(rarityLv: number): void {
  const def = BELLS[rarityLv];
  const s = settings.peek();
  if (!def || s.volMaster * s.volSfx <= 0) return;
  const a = audio();
  if (!a) return;
  const t0 = a.currentTime;
  const out = a.createGain();
  out.gain.value = def.gain * s.volMaster * s.volSfx;
  out.connect(a.destination);
  def.partials.forEach((p, i) => {
    const o = a.createOscillator();
    const g = a.createGain();
    o.type = def.type;
    o.frequency.value = def.f * p;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(1 / (i + 1), t0 + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + def.decay / (1 + i * 0.3));
    o.connect(g).connect(out);
    o.start(t0);
    o.stop(t0 + def.decay + 0.05);
  });
}

/** Rung theo bậc phản hồi: ③ 10ms · ④ 20ms · ⑤ 30-40-30ms. */
export function haptic(level: 3 | 4 | 5): void {
  if (!settings.peek().haptics || typeof navigator === 'undefined' || !navigator.vibrate) return;
  navigator.vibrate(level === 3 ? 10 : level === 4 ? 20 : [30, 40, 30]);
}
